import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Calendar, Clock, Mail, Lock, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerPortalPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [isLoading, setIsLoading] = useState(false);
  
  // Real booking data from backend
  const [bookings, setBookings] = useState([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);

  // Fetch user bookings
  const fetchBookings = async () => {
    setIsLoadingBookings(true);
    try {
      const token = authStorage.getToken();
      if (!token) {
        toast.error('Session expired. Please login again.');
        setIsLoggedIn(false);
        return;
      }
      
      const response = await axios.get(
        `${BACKEND_URL}/api/bookings/my-bookings`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setBookings(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        authStorage.clearToken();
        setIsLoggedIn(false);
      } else {
        toast.error('Failed to load bookings');
      }
    } finally {
      setIsLoadingBookings(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/login`,
        loginData
      );
      
      // Store token in localStorage
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }
      
      setCurrentUser(response.data);
      setIsLoggedIn(true);
      toast.success('Welcome back!');
      setLoginData({ email: '', password: '' });
      
      // Fetch user bookings after login
      await fetchBookings();
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        {
          name: registerData.name,
          email: registerData.email,
          password: registerData.password
        }
      );
      
      // Store token in localStorage
      if (response.data.token) {
        localStorage.setItem('auth_token', response.data.token);
      }
      
      setCurrentUser(response.data);
      setIsLoggedIn(true);
      toast.success('Account created successfully! Welcome!');
      setRegisterData({ name: '', email: '', password: '', confirmPassword: '' });
      
      // Fetch user bookings after registration
      await fetchBookings();
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = authStorage.getToken();
      if (token) {
        await axios.post(
          `${BACKEND_URL}/api/auth/logout`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      }
      authStorage.clearToken();
      setIsLoggedIn(false);
      setCurrentUser(null);
      setBookings([]);
      setLoginData({ email: '', password: '' });
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      // Still log out on frontend even if backend fails
      authStorage.clearToken();
      setIsLoggedIn(false);
      setCurrentUser(null);
      setBookings([]);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <User className="w-16 h-16 text-red-600 mx-auto mb-4" />
            <CardTitle className="text-2xl">
              {showRegister ? 'Create Account' : 'Customer Portal'}
            </CardTitle>
            <CardDescription>
              {showRegister 
                ? 'Sign up to manage your bookings and account' 
                : 'Sign in to manage your bookings and account'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!showRegister ? (
              // Login Form
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    placeholder="your@email.com"
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-red-600 hover:bg-red-700"
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            ) : (
              // Registration Form
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    required
                    value={registerData.name}
                    onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                    placeholder="John Doe"
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-email">Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    required
                    value={registerData.email}
                    onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                    placeholder="your@email.com"
                    disabled={isLoading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reg-password">Password</Label>
                  <Input
                    id="reg-password"
                    type="password"
                    required
                    value={registerData.password}
                    onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                  <p className="text-xs text-gray-500">Minimum 6 characters</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    required
                    value={registerData.confirmPassword}
                    onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                    placeholder="••••••••"
                    disabled={isLoading}
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="w-full bg-red-600 hover:bg-red-700"
                >
                  {isLoading ? 'Creating account...' : 'Create Account'}
                </Button>
              </form>
            )}
            
            <div className="mt-6 text-center">
              <Button 
                variant="link" 
                onClick={() => setShowRegister(!showRegister)}
                disabled={isLoading}
                className="text-red-600 hover:text-red-700"
              >
                {showRegister 
                  ? 'Already have an account? Sign in' 
                  : "Don't have an account? Sign up"}
              </Button>
            </div>

            <div className="mt-6 bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-blue-900 font-semibold mb-2">🔒 Customer Portal Features:</p>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• View booking history</li>
                <li>• Schedule new appointments</li>
                <li>• Manage account details</li>
                <li>• Quick rebooking options</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Logged in view
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-black text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Welcome back, {currentUser?.name}!</h1>
              <p className="text-gray-300 mt-2">{currentUser?.email}</p>
            </div>
            <Button onClick={handleLogout} variant="outline" className="border-white text-white hover:bg-white hover:text-black">
              Logout
            </Button>
          </div>
        </div>
      </section>

      {/* Dashboard Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="bookings">
            <TabsList className="grid w-full grid-cols-3 max-w-md">
              <TabsTrigger value="bookings">My Bookings</TabsTrigger>
              <TabsTrigger value="new">New Booking</TabsTrigger>
              <TabsTrigger value="account">Account</TabsTrigger>
            </TabsList>

            {/* My Bookings */}
            <TabsContent value="bookings" className="mt-6">
              <div className="space-y-4">
                <h2 className="text-2xl font-bold mb-4">Your Bookings</h2>
                
                {isLoadingBookings ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <p className="text-gray-600">Loading your bookings...</p>
                    </CardContent>
                  </Card>
                ) : bookings.length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">No Bookings Yet</h3>
                      <p className="text-gray-600 mb-4">
                        You haven't made any bookings yet. Ready to schedule your first cleaning?
                      </p>
                      <a href="/booking">
                        <Button className="bg-red-600 hover:bg-red-700">
                          Book Your First Cleaning
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                ) : (
                  bookings.map((booking, index) => {
                    // Determine if booking is upcoming or completed based on date
                    const bookingDate = new Date(booking.date);
                    const today = new Date();
                    const isUpcoming = bookingDate >= today;
                    
                    return (
                      <Card key={index}>
                        <CardContent className="p-6">
                          <div className="flex justify-between items-start">
                            <div className="space-y-2 flex-1">
                              <h3 className="text-xl font-semibold">{booking.serviceType}</h3>
                              <p className="text-sm text-gray-600">{booking.propertyType}</p>
                              <div className="flex flex-col gap-2 text-sm text-gray-600">
                                <span className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4" />
                                  {booking.date}
                                </span>
                                <span className="flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  {booking.timeSlot}
                                </span>
                              </div>
                              {booking.address && (
                                <p className="text-sm text-gray-600 mt-2">
                                  📍 {booking.address}
                                </p>
                              )}
                              {booking.specialInstructions && (
                                <p className="text-sm text-gray-500 italic mt-2">
                                  Note: {booking.specialInstructions}
                                </p>
                              )}
                            </div>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${
                              isUpcoming 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {isUpcoming ? 'Upcoming' : 'Completed'}
                            </span>
                          </div>
                          {!isUpcoming && (
                            <a href="/booking">
                              <Button className="mt-4 bg-red-600 hover:bg-red-700">
                                Book Again
                              </Button>
                            </a>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </TabsContent>

            {/* New Booking */}
            <TabsContent value="new" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Schedule a New Cleaning</CardTitle>
                  <CardDescription>Quick booking for returning customers</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Use our full booking form for complete scheduling options
                  </p>
                  <a href="/booking">
                    <Button className="bg-red-600 hover:bg-red-700">
                      Go to Booking Page
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Account Settings */}
            <TabsContent value="account" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Account Settings</CardTitle>
                  <CardDescription>Manage your account information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input value={currentUser?.name || ''} disabled />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input value={currentUser?.email || ''} disabled />
                  </div>
                  <p className="text-sm text-gray-500">
                    Need to update your account details? Contact us at tpcslex@gmail.com
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default CustomerPortalPage;
